import React, { ReactNode } from 'react'
import dayjs from 'dayjs'
import Spin from 'antd/lib/spin'
import utc from 'dayjs/plugin/utc'
import Layout from 'antd/lib/layout'
import { useDispatch, useSelector } from 'react-redux'
import Header from '@components/Layouts/Header'
import { useUser } from 'hooks'
import ControllerFooter from '@components/ControllerFooter'
import { history } from '@components/App'
import routes from '@utils/constants/routes'
import { rootActions } from '@app-store/slices'
import store, { RootState } from '@app-store/store'
import { STATUS, isDisabledFeature } from '@features/General'
import styles from './index.scss'

dayjs.extend(utc)
interface IExhibitorOnboaringLayout {
    title: string
    textTitle?: string
    children: ReactNode
    widthPage?: string
}

const getPayload = (onboarding, companyID, user, isAdmin) => {
    const payload: any = {
        isAdmin: isAdmin,
        companyID,
        userID: user._id,
        eventID: onboarding.event._id,
        avatar: onboarding.avatar
    }
    if (isAdmin) {
        payload.company = {
            sectors: {
                categories: [...onboarding.sectors]
            },
            demandingSectors: {
                categories: [...onboarding.demandingSectors]
            }
        }
        payload.logo = onboarding.logo
    }
    return payload
}

function ExhibitorOnboardingLayout(props: IExhibitorOnboaringLayout) {
    const { children, title, widthPage = '100%', textTitle = null } = props
    const dispatch = useDispatch()
    const onboarding = useSelector((state: RootState) => state.companies.onboardingExhibitor)
    const { isAdmin } = useSelector((state: RootState) => state.ui.companyProfile)
    const { data: i18n } = useSelector((state: RootState) => state.localization.locals)
    const { status: onboardingStatus } = useSelector((state: RootState) => state.companies.checkStatusEvent)
    const { status: activeFeaturesStatus } = useSelector((state: RootState) => state.eventSetting.activeFeatures)
    const { status: surveyStatus } = useSelector((state: RootState) => state.survey.list)

    const endAction = () => {
        const { companyId: companyID, user } = useUser()
        const payload = getPayload(onboarding, companyID, user, isAdmin)
        dispatch(rootActions.companies.onboardingExhibitor.onRequest(payload))
    }
    const loading = [onboardingStatus, activeFeaturesStatus, surveyStatus].includes(STATUS.RUNNING)
    return (
        <Spin spinning={loading}>
            <div style={{ display: loading ? 'null' : 'block' }}>
                <Layout className={styles['exhibitor-onboarding-layout']}>
                    <Header layoutMode="exhibitor-onboarding" />
                    <Layout.Content
                        className={styles['exhibitor-onboarding-layout__content']}
                        style={{ width: widthPage }}
                    >
                        {textTitle ? (
                            <>
                                <h1 className={styles['exhibitor-onboarding-layout__content_title_text']}>{title}</h1>
                                <h6 className={styles['exhibitor-onboarding-layout__content_text']}>{textTitle}</h6>
                            </>
                        ) : (
                            <h1 className={styles['exhibitor-onboarding-layout__content_title']}>{title}</h1>
                        )}
                        {children}
                    </Layout.Content>
                    <ControllerFooter
                        leaveAction={() => {
                            history.push(routes.organizer._)
                        }}
                        keyRoute={routes.exhibitor.onboarding._}
                        steps={makeSteps(dispatch, i18n).filter(item => item.hide !== true)}
                        endAction={endAction}
                    />
                </Layout>{' '}
            </div>
        </Spin>
    )
}

const saveITP = dispatch => {
    dispatch(rootActions.companies.onboardingExhibitor.onSaveITP())
}

export const onSubmitSyncCalendar = (data, oldUnavailables = null, userContactId) => {
    const selectedSlots = data.map(item => dayjs(item, 'YYYY-MM-DD hh:mm AM').utc())
    const deletedSlots = oldUnavailables
        .filter(
            item => !selectedSlots.find(x => dayjs(item.startDate, 'YYYY-MM-DD hh:mm AM').utc().isSame(x, 'second'))
        )
        .map(item => item._id)
    const unavailabilities = oldUnavailables.map(item => dayjs(item.startDate, 'YYYY-MM-DD hh:mm AM').utc())
    const newSelectedSlots = selectedSlots
        .filter(item => !unavailabilities.find(x => item.isSame(x, 'second')))
        .map(item => item.format())
    if (deletedSlots.length) {
        const deletePayload = {
            ids: deletedSlots,
            sagaCB: {
                onSuccess: () => {
                    if (!newSelectedSlots.length) {
                        history.push(routes.exhibitor.onboarding.profile._)
                    }
                }
            }
        }
        store.dispatch(rootActions.meetings.deleteBulkUnavailabilities.onRequest(deletePayload))
    }
    if (newSelectedSlots.length) {
        const UISyncCalendarByTokenData = {
            type: 'User',
            typeId: userContactId,
            notes: 'User Notes',
            timeslots: newSelectedSlots.map(item => ({
                startDate: dayjs(item, 'YYYY-MM-DD hh:mm AM').utc().format(),
                endDate: dayjs(item, 'YYYY-MM-DD hh:mm AM').add(15, 'm').utc().format()
            }))
        }
        const payload = {
            data: UISyncCalendarByTokenData,
            sagaCB: {
                onSuccess: () => {
                    history.push(routes.exhibitor.onboarding.profile._)
                }
            }
        }
        store.dispatch(rootActions.meetings.addToAgenda.onRequest(payload))
    }
    if (!deletedSlots.length && !newSelectedSlots.length) {
        history.push(routes.exhibitor.onboarding.profile._)
    }
}

const makeSteps = (dispatch, i18n) => {
    const onboarding = useSelector((state: RootState) => state.companies.onboardingExhibitor)
    const { status: companyUpdateStatus } = useSelector((state: RootState) => state.companies.edit)
    const { status: contactUpdateStatus } = useSelector((state: RootState) => state.contacts.edit)
    const { isMeetingHubEvent, isAdmin } = useSelector((state: RootState) => state.ui.companyProfile)
    const { data } = useSelector((state: RootState) => state.companies.checkStatusEvent)
    const { data: unavailableSlotsData } = useSelector((state: RootState) => state.meetings.getUnavailableSlots)
    const { user, contactId: userContactId } = useUser()
    const endAction = () => {
        const companyID = user?.eventInfos?.find(x => x.eventId === onboarding.event._id)?.companyId
        const payload = getPayload(onboarding, companyID, user, isAdmin)
        dispatch(rootActions.companies.onboardingExhibitor.onRequest(payload))
    }

    return [
        {
            key: 'events',
            routes: [{ route: 'events', skip: false }],
            stepText: i18n?.TradeHub?.Company_Events,
            hide: onboarding.isAdmin
        },
        {
            key: 'ITP',
            routes: [
                { route: 'ITP/location', skip: false, back: true },
                { route: 'ITP/target-type', skip: false, back: true },
                { route: 'ITP/tags', skip: false, next: { onClick: () => saveITP(dispatch) }, back: true }
            ],
            stepText: i18n?.Matching?.ITP,
            hide: !onboarding.isAdmin || isDisabledFeature('ITP')
        },
        {
            key: 'categories',
            routes: [
                {
                    route: 'categories',
                    skip: false,
                    back: true,
                    nextIsSubmit: true,
                    next: {
                        isFetching: companyUpdateStatus === STATUS.RUNNING,
                        disabled: companyUpdateStatus === STATUS.RUNNING
                    }
                }
            ],
            stepText: i18n?.General?.Categories,
            hide: !onboarding.isAdmin
        },
        {
            key: 'calendar/sync',
            routes: [
                {
                    route: 'calendar/sync',
                    skip: false,
                    back: true,
                    next: {
                        onClick: isMeetingHubEvent
                            ? undefined
                            : () =>
                                  onSubmitSyncCalendar(
                                      onboarding.selectedSlots,
                                      unavailableSlotsData?.unavailable?.filter(item => item.type === 'User'),
                                      userContactId
                                  )
                    }
                }
            ],
            stepText: i18n?.TradeHub?.Sync_Calendar,
            hide: isDisabledFeature('Sync Calendar')
        },
        {
            key: 'calendar/availability',
            routes: [{ route: 'calendar/availability', skip: false, back: true, nextIsSubmit: true }],
            stepText: i18n?.TradeHub?.AvailabilityCalendar || 'Availability Calendar',
            hide: !isMeetingHubEvent || !onboarding.isAdmin
        },
        {
            key: 'profile',
            routes: [
                {
                    route: 'profile',
                    skip: true,
                    back: true,
                    nextIsSubmit: true,
                    next: {
                        onClick: onboarding.isAdmin ? undefined : () => endAction(),
                        isFetching: [contactUpdateStatus, companyUpdateStatus].includes(STATUS.RUNNING),
                        disabled: [contactUpdateStatus, companyUpdateStatus].includes(STATUS.RUNNING)
                    }
                }
            ],
            stepText: i18n?.TradeHub?.Profile
        },
        {
            key: 'survey',
            routes: [{ route: 'survey', skip: false, back: true, nextIsSubmit: true }],
            stepText: i18n?.TradeHub?.SurveyFeedback || 'Survey Feedback',
            hide: !data?.onBoardingSurveys?.length
        },
        {
            key: 'crm',
            routes: [
                {
                    route: 'crm/list',
                    skip: false,
                    disableNext: true,
                    back: true,
                    next: {
                        onClick: () => endAction(),
                        text: i18n?.Settings?.Finish || 'Finish'
                    }
                }
            ],
            stepText: i18n?.TradeHub?.Integrations,
            hide: !onboarding.isAdmin || isDisabledFeature('CRM Integration')
        }
    ]
}

export default ExhibitorOnboardingLayout
